import React from "react";
import { defineMessage, MessageDescriptor } from "react-intl";

import { FeatureFlagNames } from "@/providers/featureFlags";
import {
  Analytics24,
  ConversionPath24,
  Help24,
  IntelligenceTowers24,
  LeaderBoard24,
  Map24,
  ModeOfTravel24,
  MotionSensorActive24,
  NotificationsActive24,
  Pallet24,
  Reader24,
  Sensor24,
  User24,
  Wifi20,
} from "@/components/ui/SvgIcon";

import { ROUTES } from "@/constants/routes";

export type MainNavChildConfig = {
  id: string;
  to: string;
  title: MessageDescriptor;
  icon?: React.ComponentType<{ className?: string }>;
  featureFlag?: FeatureFlagNames;
  featureFlags?: FeatureFlagNames[];
  hideWhenAnyFeatureFlags?: FeatureFlagNames[];
  requiredRole?: "ADMIN" | "MANAGER";
  isActive?: (pathname: string) => boolean;
};

export type MainNavItemConfig = {
  id: string;
  to: string;
  title: MessageDescriptor;
  icon: React.ComponentType<{ className?: string }>;
  featureFlag?: FeatureFlagNames;
  featureFlags?: FeatureFlagNames[];
  hideForDemoKitOrg?: boolean;
  isActive?: (pathname: string) => boolean;
  children?: MainNavChildConfig[];
};

const towersTripViewMessage = defineMessage({
  defaultMessage: "Trip View",
  description: "Nested sidebar link under Intelligence Towers for Trip View.",
  id: "sidebar.nav.towers.tripView",
});

const towersDeliveryPerformanceMessage = defineMessage({
  defaultMessage: "Delivery Performance",
  description:
    "Nested sidebar link under Intelligence Towers for Delivery Performance.",
  id: "sidebar.nav.towers.deliveryPerformance",
});

const towersModelPerformanceMessage = defineMessage({
  defaultMessage: "Model Performance",
  description:
    "Nested sidebar link under Intelligence Towers for Model Performance.",
  id: "sidebar.nav.towers.modelPerformance",
});

const towersStatusTrackerMessage = defineMessage({
  defaultMessage: "Status Tracker",
  description:
    "Nested sidebar link under Intelligence Towers for Status Tracker.",
  id: "sidebar.nav.towers.statusTracker",
});

const towersAssetViewMessage = defineMessage({
  defaultMessage: "Asset View",
  description: "Nested sidebar link under Intelligence Towers for Asset View.",
  id: "sidebar.nav.towers.assetView",
});

const towersScheduleAdherenceMessage = defineMessage({
  defaultMessage: "Schedule Adherence",
  description:
    "Nested sidebar link under Intelligence Towers for Schedule Adherence.",
  id: "sidebar.nav.towers.scheduleAdherence",
});

const systemSensorsMessage = defineMessage({
  defaultMessage: "Sensors",
  description: "Nested sidebar link under System for the Sensors page.",
  id: "sidebar.nav.system.sensors",
});

const systemReadersMessage = defineMessage({
  defaultMessage: "Readers",
  description: "Nested sidebar link under System for the Readers page.",
  id: "sidebar.nav.system.readers",
});

const systemStandbySensorsMessage = defineMessage({
  defaultMessage: "Standby Sensors",
  description: "Nested sidebar link under System for Standby Sensors.",
  id: "sidebar.nav.system.standbySensors",
});

const systemWifiProvisioningMessage = defineMessage({
  defaultMessage: "Wifi Provisioning",
  description: "Nested sidebar link under System for Wifi Provisioning.",
  id: "sidebar.nav.system.wifiProvisioning",
});

const systemMapFeaturesMessage = defineMessage({
  defaultMessage: "Map Features",
  description: "Nested sidebar link under System for Map Features.",
  id: "sidebar.nav.system.mapFeatures",
});

const systemUsersMessage = defineMessage({
  defaultMessage: "Users",
  description: "Nested sidebar link under System for user management.",
  id: "sidebar.nav.system.users",
});

const systemNavItem: MainNavItemConfig = {
  id: "system",
  to: ROUTES.SYSTEM,
  title: defineMessage({
    defaultMessage: "System",
    description: "Navigation link label in sidebar for accessing sensors.",
  }),
  icon: MotionSensorActive24,
  isActive: (pathname) => pathname.includes(ROUTES.SYSTEM),
  children: [
    {
      id: "system-sensors",
      to: ROUTES.SYSTEM,
      title: systemSensorsMessage,
      icon: Sensor24,
      isActive: (pathname) =>
        pathname === ROUTES.SYSTEM || pathname === `${ROUTES.SYSTEM}/`,
    },
    {
      id: "system-readers",
      to: ROUTES.READERS,
      title: systemReadersMessage,
      icon: Reader24,
      featureFlag: FeatureFlagNames.readersPage,
      isActive: (pathname) => pathname.startsWith(ROUTES.READERS),
    },
    {
      id: "system-standby-sensors",
      to: ROUTES.STANDBY_SENSORS,
      title: systemStandbySensorsMessage,
      icon: MotionSensorActive24,
      featureFlag: FeatureFlagNames.ShowStandBySensors,
      isActive: (pathname) => pathname.startsWith(ROUTES.STANDBY_SENSORS),
    },
    {
      id: "system-wifi-provisioning",
      to: ROUTES.WIFI_PROVISIONING,
      title: systemWifiProvisioningMessage,
      icon: Wifi20,
      featureFlag: FeatureFlagNames.summitScoutWifiRemoteProvisioning,
      isActive: (pathname) => pathname.startsWith(ROUTES.WIFI_PROVISIONING),
    },
    {
      id: "system-map-features",
      to: `${ROUTES.SYSTEM}/mapFeatures`,
      title: systemMapFeaturesMessage,
      icon: Map24,
      isActive: (pathname) =>
        pathname.startsWith(`${ROUTES.SYSTEM}/mapFeatures`),
    },
    {
      id: "system-users",
      to: `${ROUTES.SYSTEM}/users`,
      title: systemUsersMessage,
      icon: User24,
      featureFlag: FeatureFlagNames.ShowUsersManagement,
      requiredRole: "MANAGER",
      isActive: (pathname) => pathname.startsWith(`${ROUTES.SYSTEM}/users`),
    },
  ],
};

const mainNavItems: MainNavItemConfig[] = [
  {
    id: "assets",
    to: ROUTES.ASSETS,
    title: defineMessage({
      defaultMessage: "Assets",
      description:
        "Navigation link label in sidebar for accessing customer assets.",
    }),
    icon: Pallet24,
    hideForDemoKitOrg: true,
    isActive: (pathname) => pathname.includes(ROUTES.ASSETS),
  },
  {
    id: "trips",
    to: ROUTES.TRIPS,
    title: defineMessage({
      defaultMessage: "Trips",
      description: "Navigation link label in sidebar for accessing trips.",
    }),
    icon: ModeOfTravel24,
    isActive: (pathname) => pathname.includes(ROUTES.TRIPS),
  },
  {
    id: "alerts",
    to: ROUTES.ALERTS,
    title: defineMessage({
      defaultMessage: "Alerts",
      description: "Navigation link label in sidebar for accessing alerts.",
    }),
    icon: NotificationsActive24,
    isActive: (pathname) => pathname.includes(ROUTES.ALERTS),
  },
  {
    id: "reports",
    to: ROUTES.REPORTS,
    title: defineMessage({
      defaultMessage: "Reports",
      description: "Navigation link label in sidebar for accessing reports.",
    }),
    icon: LeaderBoard24,
    hideForDemoKitOrg: true,
    isActive: (pathname) => pathname.includes(ROUTES.REPORTS),
  },
  {
    id: "towers",
    to: ROUTES.TOWERS,
    title: defineMessage({
      defaultMessage: "Intelligence Towers",
      description:
        "Navigation link label in sidebar for accessing Intelligence Towers.",
    }),
    icon: IntelligenceTowers24,
    featureFlags: [
      FeatureFlagNames.IntelligenceTowers,
      FeatureFlagNames.TripIntelligenceTowerMarketing,
    ],
    isActive: (pathname) => pathname.includes(ROUTES.TOWERS),
    children: [
      {
        id: "towers-trip-view",
        to: `${ROUTES.TOWERS}/trip`,
        title: towersTripViewMessage,
        icon: ModeOfTravel24,
        featureFlag: FeatureFlagNames.IntelligenceTowerTripView,
        isActive: (pathname) =>
          pathname === `${ROUTES.TOWERS}/trip` ||
          pathname.startsWith(`${ROUTES.TOWERS}/trip/`),
      },
      {
        id: "towers-delivery-performance",
        to: `${ROUTES.TOWERS}/delivery-performance`,
        title: towersDeliveryPerformanceMessage,
        icon: LeaderBoard24,
        featureFlags: [
          FeatureFlagNames.TripIntelligenceTowerMarketing,
          FeatureFlagNames.IntelligenceTowersDeliveryPerformance,
        ],
        isActive: (pathname) =>
          pathname.startsWith(`${ROUTES.TOWERS}/delivery-performance`),
      },
      {
        id: "towers-model-performance",
        to: `${ROUTES.TOWERS}/model-performance`,
        title: towersModelPerformanceMessage,
        icon: Analytics24,
        featureFlags: [
          FeatureFlagNames.TripIntelligenceTowerMarketing,
          FeatureFlagNames.IntelligenceTowersDiagnostics,
        ],
        isActive: (pathname) =>
          pathname.startsWith(`${ROUTES.TOWERS}/model-performance`),
      },
      {
        id: "towers-status-tracker",
        to: `${ROUTES.TOWERS}/status-tracker`,
        title: towersStatusTrackerMessage,
        icon: Pallet24,
        featureFlag: FeatureFlagNames.AssetTower,
        featureFlags: [FeatureFlagNames.AssetTowerInventoryTracker],
        isActive: (pathname) =>
          pathname.startsWith(`${ROUTES.TOWERS}/status-tracker`),
      },
      {
        id: "towers-asset-view",
        to: `${ROUTES.TOWERS}/asset-view`,
        title: towersAssetViewMessage,
        icon: Map24,
        featureFlag: FeatureFlagNames.AssetTower,
        featureFlags: [FeatureFlagNames.AssetTowerRealTimeViz],
        isActive: (pathname) =>
          pathname.startsWith(`${ROUTES.TOWERS}/asset-view`),
      },
      {
        id: "towers-schedule-adherence",
        to: `${ROUTES.TOWERS}/schedule-adherence`,
        title: towersScheduleAdherenceMessage,
        icon: LeaderBoard24,
        featureFlag: FeatureFlagNames.AssetTower,
        featureFlags: [FeatureFlagNames.AssetTowerHistoricalPerformance],
        isActive: (pathname) =>
          pathname.startsWith(`${ROUTES.TOWERS}/schedule-adherence`),
      },
    ],
  },
];

const bottomNavItems: MainNavItemConfig[] = [
  {
    id: "pairing",
    to: ROUTES.PAIR_LOGIN,
    title: defineMessage({
      defaultMessage: "Pairing",
      description: "Navigation link label in sidebar for accessing pairings.",
    }),
    icon: ConversionPath24,
    hideForDemoKitOrg: true,
    isActive: () => false,
  },
  {
    id: "support",
    to: ROUTES.SUPPORT,
    title: defineMessage({
      defaultMessage: "Support",
      description: "Navigation link label in sidebar for accessing support.",
    }),
    icon: Help24,
    isActive: (pathname: string) => pathname.includes(ROUTES.SUPPORT),
  },
];

// System sits with the primary destinations in the redesigned menu, where it is
// an expandable section, and above Pairing in the legacy menu, where it is a
// plain link.
const withoutHiddenDemoKitPages = (
  items: MainNavItemConfig[],
  hideDemoKitPages: boolean,
) =>
  hideDemoKitPages ? items.filter((item) => !item.hideForDemoKitOrg) : items;

export const getMainNavItems = (
  showSubpagesInSideMenu: boolean,
  hideDemoKitPages = false,
): MainNavItemConfig[] =>
  withoutHiddenDemoKitPages(
    showSubpagesInSideMenu ? [...mainNavItems, systemNavItem] : mainNavItems,
    hideDemoKitPages,
  );

export const getBottomNavItems = (
  showSubpagesInSideMenu: boolean,
  hideDemoKitPages = false,
): MainNavItemConfig[] =>
  withoutHiddenDemoKitPages(
    showSubpagesInSideMenu
      ? bottomNavItems
      : [systemNavItem, ...bottomNavItems],
    hideDemoKitPages,
  );

export const allNavItems: MainNavItemConfig[] = [
  ...mainNavItems,
  systemNavItem,
  ...bottomNavItems,
];
